// AdminPortfolioWorkspace — focused workspace for each major account
// (Chew Flats, Wister Apts, Raza Homes). Tabs: Overview / Jobs / Turns
// / Inbox / Punchlist / Documents / Reports / Settings. Non-Overview
// tabs route to existing global pages pre-filtered to this portfolio.
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { AdminShell as Layout } from "@/components/Layout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { House, Buildings, ListChecks, ChatCircleText, ClipboardText,
         FileText, ChartLineUp, Gear, Warning, CheckCircle, Clock,
         ArrowRight, Repeat, Sparkle, DownloadSimple, Envelope } from "@phosphor-icons/react";

const TAB_DEFS = [
  { key: "overview",  label: "Overview",  icon: House },
  { key: "jobs",      label: "Jobs",      icon: ListChecks },
  { key: "turns",     label: "Turns",     icon: Repeat },
  { key: "inbox",     label: "Messages",  icon: ChatCircleText },
  { key: "punchlist", label: "Punchlist", icon: ClipboardText },
  { key: "docs",      label: "Documents", icon: FileText },
  { key: "reports",   label: "Reports",   icon: ChartLineUp },
  { key: "settings",  label: "Settings",  icon: Gear },
];

// Per-portfolio hero thumbnails. Curated brand imagery — swap in a
// property photo per slug. Backend `portfolio.hero_image_url` overrides.
const HERO_THUMBS = {
  "chew-flats": "https://customer-assets-jt897jd0.emergentagent.net/job_repair-tracker-pro-25/artifacts/68b5eu0c_20260826_164842~2.webp",
  "wister-apts": "https://customer-assets-jt897jd0.emergentagent.net/job_repair-tracker-pro-25/artifacts/3df5vhf0_Screenshot_20260826_183949_Google%20Earth~2.jpg",
  "raza-homes":  "https://customer-assets-jt897jd0.emergentagent.net/job_repair-tracker-pro-25/artifacts/i2tp0x8g_images%20%281%29.jpeg",
};

export default function AdminPortfolioWorkspace() {
  const { slug } = useParams();
  const nav = useNavigate();
  const [portfolio, setPortfolio] = useState(null);
  const [others, setOthers] = useState([]);
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    api.get("/major-portfolios").then(r => {
      setOthers((r.data.portfolios || []).filter(p => p.slug !== slug));
    });
    api.get(`/major-portfolios/${slug}`).then(r => setPortfolio(r.data.portfolio))
      .catch(() => toast.error("Portfolio not found"));
  }, [slug]);

  if (!portfolio) return <Layout><div className="p-6 text-slate-400">Loading…</div></Layout>;

  const brand = portfolio.brand_color || "#0f172a";

  return (
    <Layout>
      <div className="max-w-[1400px] mx-auto" data-testid="portfolio-workspace">
        {/* Hero */}
        <div style={{ background: `linear-gradient(135deg, ${brand}, ${brand}dd)` }} className="text-white px-4 sm:px-6 py-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              {(portfolio.hero_image_url || HERO_THUMBS[slug]) && (
                <img src={portfolio.hero_image_url || HERO_THUMBS[slug]}
                  alt={portfolio.name}
                  data-testid="portfolio-hero-thumb"
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-lg object-cover border-2 border-white/40 shadow-lg flex-none"/>
              )}
              <div className="min-w-0">
                <div className="text-[10px] font-black uppercase tracking-[2px] opacity-80">Major Portfolio</div>
                <h1 className="text-2xl sm:text-3xl font-black leading-tight mt-1 truncate">{portfolio.name}</h1>
                <div className="text-sm opacity-90 mt-1">{portfolio.subtitle}{portfolio.landlord_name ? ` · ${portfolio.landlord_name}` : ""}</div>
              </div>
            </div>
            {others.length > 0 && (
              <div className="flex items-center gap-2">
                <span className="text-[10px] uppercase font-black tracking-widest opacity-70">Switch:</span>
                {others.map(o => (
                  <Link key={o.slug} to={`/admin/portfolios/${o.slug}`}
                    className="text-xs font-black uppercase tracking-wider px-3 py-1.5 rounded-md bg-white/15 hover:bg-white/25 transition-colors" data-testid={`portfolio-switch-${o.slug}`}>
                    {o.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Tab strip */}
        <div className="bg-white border-b border-slate-200 sticky top-0 z-10 overflow-x-auto">
          <div className="flex items-center px-2 sm:px-4">
            {TAB_DEFS.map(t => {
              const Ic = t.icon;
              const active = tab === t.key;
              return (
                <button key={t.key} onClick={() => setTab(t.key)}
                  className={`flex items-center gap-1.5 px-3 sm:px-4 py-3 text-xs font-black uppercase tracking-wider whitespace-nowrap border-b-2 transition-colors ${
                    active ? "border-slate-900 text-slate-900" : "border-transparent text-slate-500 hover:text-slate-800"
                  }`} data-testid={`portfolio-tab-${t.key}`}>
                  <Ic weight={active ? "fill" : "regular"} size={14}/> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {tab === "overview"  && <OverviewTab slug={slug} brand={brand} onGoTab={setTab}/>}
          {tab === "jobs"      && <JobsTab slug={slug}/>}
          {tab === "turns"     && <ExternalRedirect label="Turns" to="/admin/turns" note="Global Turns board (filter by address)"/>}
          {tab === "inbox"     && <ExternalRedirect label="Messages" to="/admin/chew-tickets" note={slug === "chew-flats" ? "Chew Flats tenant inbox" : "Coming soon — global inbox by portfolio"}/>}
          {tab === "punchlist" && <ExternalRedirect label="Walk Punchlist" to="/admin/chew-punchlist" note={slug === "chew-flats" ? "Chew Flats walk-throughs" : "Walk punchlist scoped by portfolio (Chew Flats only for now)"}/>}
          {tab === "docs"      && <ExternalRedirect label="Documents" to="/admin/esign" note="E-Sign hub — leases, addendums, quotes"/>}
          {tab === "reports"   && <ExternalRedirect label="Reports" to="/admin/reports" note="Global reports (filter by address)"/>}
          {tab === "settings"  && <SettingsTab portfolio={portfolio} onSaved={p => setPortfolio(p)}/>}
        </div>
      </div>
    </Layout>
  );
}

function OverviewTab({ slug, brand, onGoTab }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/major-portfolios/${slug}/overview`).then(r => setData(r.data))
      .catch(() => toast.error("Failed to load overview"));
  }, [slug]);
  if (!data) return <div className="text-slate-400">Loading…</div>;
  const s = data.stats;
  const health = s.health_grade;
  const healthColor = health === "A" ? "emerald" : health === "B" ? "cyan" : health === "C" ? "amber" : "red";

  return (
    <div className="space-y-6" data-testid="portfolio-overview">
      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Units" value={s.units_total} subtitle={`${s.units_occupied} occupied · ${s.occupancy_pct}%`}/>
        <StatCard label="Open jobs" value={s.jobs_open} subtitle={s.jobs_urgent ? `${s.jobs_urgent} urgent` : "clear"} accent={s.jobs_urgent ? "red" : undefined}/>
        <StatCard label="Punchlist open" value={s.punchlist_open} subtitle="across all walks" accent={s.punchlist_open > 20 ? "amber" : undefined}/>
        <StatCard label="Health" value={health} subtitle={`${s.jobs_completed_30d} done · 30 days`} accent={healthColor}/>
      </div>

      {/* Recent activity */}
      <div>
        <h2 className="text-[10px] uppercase font-black tracking-widest text-slate-500 mb-2">Recent activity</h2>
        <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
          {(data.recent_activity || []).length === 0 && (
            <div className="p-6 text-center text-slate-400 text-sm italic">No recent jobs.</div>
          )}
          {(data.recent_activity || []).map(j => (
            <Link key={j.id} to={`/admin/jobs/${j.id}`}
              className="flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors"
              data-testid={`portfolio-recent-${j.id}`}>
              <div className={`w-1.5 h-8 rounded-full ${j.priority === "urgent" ? "bg-red-500" : j.priority === "high" ? "bg-orange-500" : "bg-slate-300"}`}/>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-slate-900 truncate">{j.title || "(untitled)"}</div>
                <div className="text-xs text-slate-500">Unit {j.unit || "—"} · {j.status}</div>
              </div>
              <ArrowRight size={16} className="text-slate-300"/>
            </Link>
          ))}
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <QuickAction onClick={() => onGoTab?.("jobs")} label={`All ${slug === "chew-flats" ? "Chew Flats" : "portfolio"} jobs`} Ic={ListChecks}/>
        <QuickAction onClick={() => onGoTab?.("punchlist")} label="Punchlists" Ic={ClipboardText}/>
        <QuickAction to="/admin/inspections" label="InspectRadar" Ic={Sparkle}/>
        <QuickAction onClick={() => onGoTab?.("turns")} label="Turns board" Ic={Repeat}/>
      </div>
    </div>
  );
}

function StatCard({ label, value, subtitle, accent }) {
  const cls = {
    red: "bg-red-50 border-red-200 text-red-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    cyan: "bg-cyan-50 border-cyan-200 text-cyan-900",
  }[accent] || "bg-white border-slate-200 text-slate-900";
  return (
    <div className={`rounded-xl border ${cls} p-4`}>
      <div className="text-3xl sm:text-4xl font-black leading-none">{value}</div>
      <div className="mt-1.5 text-[10px] font-black uppercase tracking-widest opacity-70">{label}</div>
      {subtitle && <div className="mt-1 text-[10px] font-medium opacity-60">{subtitle}</div>}
    </div>
  );
}

function QuickAction({ to, onClick, label, Ic }) {
  const inner = (
    <>
      <Ic weight="fill" size={20} className="text-slate-700"/>
      <div className="text-[11px] font-black uppercase tracking-widest text-slate-700 text-center">{label}</div>
    </>
  );
  const cls = "rounded-xl bg-white border border-slate-200 p-3 hover:bg-slate-50 hover:border-slate-300 transition-colors flex flex-col items-center gap-1.5";
  if (onClick) {
    return <button onClick={onClick} className={cls} data-testid={`portfolio-quick-${(label||"").toLowerCase().replace(/\s+/g,"-")}`}>{inner}</button>;
  }
  return <Link to={to} className={cls} data-testid={`portfolio-quick-${(label||"").toLowerCase().replace(/\s+/g,"-")}`}>{inner}</Link>;
}

function JobsTab({ slug }) {
  const [jobs, setJobs] = useState([]);
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const q = status ? `?status=${status}` : "";
    api.get(`/major-portfolios/${slug}/jobs${q}`).then(r => {
      setJobs(r.data.jobs || []);
      setSelected(new Set());  // reset on filter change
    });
  }, [slug, status]);

  const toggle = (id) => {
    setSelected(prev => {
      const n = new Set(prev);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });
  };
  const selectAll = () => {
    setSelected(prev => prev.size === jobs.length ? new Set() : new Set(jobs.map(j => j.id)));
  };
  const clearSel = () => setSelected(new Set());

  const bulkDownload = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    setBusy(true);
    const t = toast.loading(`Building ${ids.length} PDF${ids.length === 1 ? "" : "s"}…`);
    try {
      const r = await api.post("/jobs/bulk-pdf-zip", { job_ids: ids },
        { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/zip" }));
      const a = document.createElement("a");
      a.href = url; a.download = `${slug}-work-orders-${ids.length}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${ids.length}`, { id: t });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed", { id: t });
    } finally { setBusy(false); }
  };
  const bulkEmail = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const to = window.prompt(`Email ${ids.length} certified WO PDF${ids.length === 1 ? "" : "s"} to:`, "");
    if (!to || !to.includes("@")) {
      if (to !== null) toast.error("Enter a valid email");
      return;
    }
    const note = window.prompt("Add a note (optional):", "") || "";
    setBusy(true);
    const t = toast.loading(`Emailing to ${to}…`);
    try {
      await api.post("/jobs/bulk-email-pdfs",
        { job_ids: ids, to: to.trim(), note, customer: true },
        { timeout: 120000 });
      toast.success(`Sent ${ids.length} to ${to}`, { id: t });
      clearSel();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Email failed", { id: t });
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="portfolio-jobs">
      <div className="flex gap-2 items-center flex-wrap">
        {["", "pool", "assigned", "in_progress", "completed"].map(s => (
          <button key={s || "all"} onClick={() => setStatus(s)}
            className={`text-[11px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full ${
              status === s ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:bg-slate-200"
            }`} data-testid={`portfolio-jobs-filter-${s || "all"}`}>
            {s ? s.replace("_", " ") : "All"}
          </button>
        ))}
        <div className="ml-auto text-xs text-slate-500 font-bold">{jobs.length} jobs</div>
      </div>

      {/* Bulk toolbar — sticky on mobile so the buttons stay reachable
          while scrolling a long list of Chew completed jobs. */}
      {selected.size > 0 && (
        <div
          data-testid="portfolio-jobs-bulk-bar"
          className="sticky top-2 z-20 bg-slate-900 text-white rounded-lg px-3 py-2 flex items-center gap-2 flex-wrap shadow-lg">
          <div className="text-xs font-black uppercase tracking-widest">
            {selected.size} selected
          </div>
          <button onClick={clearSel}
            className="text-[10px] font-black uppercase tracking-widest text-slate-300 hover:text-white">
            Clear
          </button>
          <div className="ml-auto flex gap-2 flex-wrap">
            <button onClick={bulkDownload} disabled={busy}
              data-testid="portfolio-jobs-bulk-pdf"
              className="h-8 px-3 bg-emerald-500 hover:bg-emerald-400 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 rounded disabled:opacity-60">
              <DownloadSimple weight="bold" size={12}/> Download PDFs
            </button>
            <button onClick={bulkEmail} disabled={busy}
              data-testid="portfolio-jobs-bulk-email"
              className="h-8 px-3 bg-indigo-500 hover:bg-indigo-400 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1 rounded disabled:opacity-60">
              <Envelope weight="bold" size={12}/> Email PDFs
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {jobs.length > 0 && (
          <div className="flex items-center gap-3 px-3 py-2 bg-slate-50 rounded-t-xl">
            <input
              type="checkbox"
              checked={selected.size > 0 && selected.size === jobs.length}
              onChange={selectAll}
              data-testid="portfolio-jobs-select-all"
              className="h-4 w-4 accent-slate-900 cursor-pointer"/>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-500">
              Select all
            </div>
          </div>
        )}
        {jobs.length === 0 && <div className="p-6 text-center text-slate-400 text-sm italic">No jobs match.</div>}
        {jobs.map(j => (
          <div key={j.id}
            className={`flex items-center gap-3 p-3 hover:bg-slate-50 transition-colors ${
              selected.has(j.id) ? "bg-slate-50" : ""
            }`}>
            <input
              type="checkbox"
              checked={selected.has(j.id)}
              onChange={() => toggle(j.id)}
              onClick={e => e.stopPropagation()}
              data-testid={`portfolio-job-check-${j.id}`}
              className="h-4 w-4 accent-slate-900 cursor-pointer flex-shrink-0"/>
            <Link to={`/admin/jobs/${j.id}`} className="flex items-center gap-3 flex-1 min-w-0">
              <div className={`w-1.5 h-10 rounded-full flex-shrink-0 ${j.priority === "urgent" ? "bg-red-500" : j.priority === "high" ? "bg-orange-500" : "bg-slate-300"}`}/>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm text-slate-900 truncate">{j.title || "(untitled)"}</div>
                <div className="text-xs text-slate-500 truncate">{j.address} {j.unit ? `· Unit ${j.unit}` : ""} · {j.status}</div>
              </div>
              <ArrowRight size={16} className="text-slate-300 flex-shrink-0"/>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
}

function ExternalRedirect({ label, to, note }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-6 text-center" data-testid="portfolio-external-redirect">
      <div className="text-lg font-black">{label}</div>
      <p className="text-sm text-slate-500 mt-1">{note}</p>
      <Link to={to} className="mt-4 inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-lg px-4 py-2.5 text-xs font-black uppercase tracking-wider">
        Open → <ArrowRight size={14}/>
      </Link>
    </div>
  );
}

function SettingsTab({ portfolio, onSaved }) {
  const [f, setF] = useState(portfolio);
  const [busy, setBusy] = useState(false);
  const [addrDraft, setAddrDraft] = useState("");
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/major-portfolios/${portfolio.slug}`, {
        slug: portfolio.slug,  // ignored server-side but required by schema
        name: f.name, subtitle: f.subtitle,
        landlord_name: f.landlord_name,
        address_keys: f.address_keys,
        brand_color: f.brand_color,
        unit_count: f.unit_count,
        status: f.status,
        sort_order: f.sort_order,
        email_match: f.email_match || [],
        wo_prefix: f.wo_prefix || "",
        access_notes: f.access_notes || "",
      });
      toast.success("Saved");
      onSaved({ ...portfolio, ...f });
    } catch (e) { toast.error("Save failed"); }
    finally { setBusy(false); }
  };
  const addAddr = () => {
    const norm = addrDraft.trim().toLowerCase();
    if (!norm) return;
    if (f.address_keys.includes(norm)) { toast.error("Already added"); return; }
    setF({ ...f, address_keys: [...f.address_keys, norm] });
    setAddrDraft("");
  };
  const [matchDraft, setMatchDraft] = useState("");
  const addMatch = () => {
    const m = matchDraft.trim().toLowerCase();
    if (!m) return;
    if ((f.email_match || []).includes(m)) { toast.error("Already added"); return; }
    setF({ ...f, email_match: [...(f.email_match || []), m] });
    setMatchDraft("");
  };
  const backfill = async () => {
    setBusy(true);
    try {
      await save();
      const r = await api.post(`/major-portfolios/${portfolio.slug}/backfill-inbox`);
      toast.success(`Tagged ${r.data.tagged} existing WO${r.data.tagged === 1 ? "" : "s"} → ${f.name}`);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Backfill failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-xl space-y-4" data-testid="portfolio-settings">
      <Field label="Display name"><input value={f.name} onChange={e=>setF({...f, name:e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2" data-testid="ps-name"/></Field>
      <Field label="Subtitle"><input value={f.subtitle} onChange={e=>setF({...f, subtitle:e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2" data-testid="ps-subtitle"/></Field>
      <Field label="Landlord / master tenant"><input value={f.landlord_name} onChange={e=>setF({...f, landlord_name:e.target.value})} className="w-full border border-slate-300 rounded-lg px-3 py-2" data-testid="ps-landlord"/></Field>
      <Field label="Brand color">
        <div className="flex gap-2 items-center">
          <input type="color" value={f.brand_color} onChange={e=>setF({...f, brand_color:e.target.value})} className="h-10 w-14 border border-slate-300 rounded-lg" data-testid="ps-color"/>
          <input value={f.brand_color} onChange={e=>setF({...f, brand_color:e.target.value})} className="flex-1 font-mono border border-slate-300 rounded-lg px-3 py-2"/>
        </div>
      </Field>
      <Field label="Addresses in this portfolio">
        <div className="space-y-2">
          {(f.address_keys || []).map(a => (
            <div key={a} className="flex items-center gap-2 bg-slate-50 rounded-lg px-3 py-2 text-sm">
              <span className="flex-1 font-mono">{a}</span>
              <button onClick={()=>setF({...f, address_keys: f.address_keys.filter(x=>x!==a)})} className="text-red-500 text-xs font-bold hover:underline">Remove</button>
            </div>
          ))}
          <div className="flex gap-2">
            <input value={addrDraft} onChange={e=>setAddrDraft(e.target.value)} placeholder="e.g. 5346 chew ave" className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="ps-addr-draft"/>
            <button onClick={addAddr} className="bg-slate-900 text-white rounded-lg px-4 text-sm font-black" data-testid="ps-addr-add">Add</button>
          </div>
          <p className="text-[10px] text-slate-500">Enter the normalized address_key (lowercase, no punctuation) — matches what appears on property_configs.</p>
        </div>
      </Field>
      <Field label="Access notes · shown to crew on Sweeper Radar + WO">
        <input value={f.access_notes || ""} onChange={e=>setF({...f, access_notes: e.target.value.slice(0, 300)})}
          placeholder="e.g. Keys at JSC office · ButterflyMX entry — call office for code"
          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="ps-access-notes"/>
      </Field>
      <Field label="Separate AppFolio account? · inbox routing">
        <div className="space-y-2 bg-amber-50 border border-amber-200 rounded-lg p-3">
          <p className="text-[11px] text-slate-600">If this portfolio's work orders come from a <b>different AppFolio account</b> into the same Gmail, list a piece of the sender name/email here. Matching WOs get tagged with this portfolio and their WO# prefixed to avoid collisions (e.g. <code>JSCRE-338-1</code>).</p>
          <div className="flex flex-wrap gap-1.5">
            {(f.email_match || []).map(m => (
              <span key={m} className="inline-flex items-center gap-1 bg-white border border-slate-300 rounded-full px-2.5 py-1 text-xs font-mono">
                {m}
                <button onClick={()=>setF({...f, email_match: f.email_match.filter(x=>x!==m)})} className="text-red-500 font-bold" aria-label="remove">×</button>
              </span>
            ))}
          </div>
          <div className="flex gap-2">
            <input value={matchDraft} onChange={e=>setMatchDraft(e.target.value)} placeholder='e.g. "JSC Real Estate" or "@jscre.com"'
              onKeyDown={e => { if (e.key === "Enter") addMatch(); }}
              className="flex-1 border border-slate-300 rounded-lg px-3 py-2 text-sm" data-testid="ps-match-draft"/>
            <button onClick={addMatch} className="bg-slate-900 text-white rounded-lg px-4 text-sm font-black" data-testid="ps-match-add">Add</button>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-600 whitespace-nowrap">WO# prefix</span>
            <input value={f.wo_prefix || ""} onChange={e=>setF({...f, wo_prefix: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 8)})}
              placeholder="JSCRE" className="w-28 border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono uppercase" data-testid="ps-wo-prefix"/>
            <button onClick={backfill} disabled={busy || !(f.email_match || []).length}
              title="Re-tag WOs already ingested from matching emails"
              className="ml-auto h-9 px-3 border-2 border-amber-500 text-amber-800 bg-white hover:bg-amber-100 rounded-lg text-[11px] font-black uppercase tracking-wider disabled:opacity-50" data-testid="ps-backfill">
              Tag existing WOs
            </button>
          </div>
        </div>
      </Field>
      <div className="flex justify-end">
        <button onClick={save} disabled={busy} className="bg-slate-900 text-white rounded-lg px-6 py-2.5 font-black text-sm disabled:opacity-50" data-testid="ps-save">{busy ? "Saving…" : "Save"}</button>
      </div>

      <BulkAuditPanel slug={portfolio.slug}/>
      <DedupePanel slug={portfolio.slug}/>
    </div>
  );
}

function DedupePanel({ slug }) {
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState("");
  const [dryRun, setDryRun] = useState(true);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const run = async () => {
    if (!secret) { toast.error("Paste the admin secret first"); return; }
    setBusy(true); setReport(null);
    try {
      const r = await api.post("/admin/portfolio-dedupe/run",
        { apply: !dryRun }, { headers: { "X-Admin-Secret": secret } });
      setReport(r.data.report);
      toast.success(dryRun ? "Dry-run complete" : "Dedupe applied");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed — endpoint enabled?"));
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className="mt-3">
        <button onClick={() => setOpen(true)}
          data-testid="dedupe-open"
          className="w-full h-11 border-2 border-rose-500 bg-rose-50 text-rose-800 font-black uppercase text-[11px] tracking-widest hover:bg-rose-100">
          🧹 Cleanup Duplicates (Properties + Jobs + Unit Unit)
        </button>
      </div>
    );
  }

  return (
    <div className="mt-3 border-2 border-rose-500 bg-rose-50 p-3 space-y-3" data-testid="dedupe-panel">
      <div className="flex items-center gap-2">
        <div className="font-black uppercase text-[11px] tracking-widest text-rose-900">Cleanup — {slug}</div>
        <button onClick={() => setOpen(false)}
          className="ml-auto h-7 w-7 border-2 border-slate-300 bg-white font-black">×</button>
      </div>
      <input type="password" value={secret} onChange={e => setSecret(e.target.value)}
        data-testid="dedupe-secret"
        placeholder="Same admin secret you use for Bulk Audit"
        className="w-full h-10 px-2 border-2 border-slate-300 text-sm font-mono"/>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={dryRun} onChange={e => setDryRun(e.target.checked)}/>
        <span>Dry-run only (uncheck to actually delete duplicates)</span>
      </label>
      <button onClick={run} disabled={busy || !secret}
        data-testid="dedupe-run"
        className="w-full h-11 bg-rose-600 hover:bg-rose-700 text-white font-black uppercase text-[11px] tracking-widest disabled:opacity-40">
        {busy ? "Running…" : dryRun ? "Preview Duplicates" : "Delete Duplicates"}
      </button>
      {report && (
        <div className="p-3 border-2 border-emerald-400 bg-emerald-50 text-xs space-y-2" data-testid="dedupe-report">
          <div className="font-black uppercase text-[10px] tracking-widest text-emerald-800">
            {report.apply ? "Applied" : "Dry-run"}
          </div>

          {/* property_configs */}
          {report.property_configs && (
            <div className="border-t border-emerald-300 pt-2">
              <div className="font-black text-emerald-900" data-testid="dedupe-pc-summary">
                Properties (property_configs)
              </div>
              <div>Duplicate groups: <b>{report.property_configs.duplicate_groups}</b></div>
              <div>Duplicates found: <b>{report.property_configs.duplicates_found}</b></div>
              <div>Archived this run: <b>{report.property_configs.archived}</b></div>
              {report.property_configs.samples?.length > 0 && (
                <div className="mt-1 pl-2 border-l-2 border-emerald-400">
                  <div className="text-[10px] uppercase text-emerald-700 font-black">Preview samples</div>
                  {report.property_configs.samples.map((s, i) => (
                    <div key={i} className="mt-1" data-testid={`dedupe-pc-sample-${i}`}>
                      <div><b>{s.address || s.address_key}</b> — {s.count}x</div>
                      <div className="text-[10px] text-slate-600">
                        keep {s.keep_id.slice(-6)} (score {s.richness_kept}) · drop {s.archive_ids.length}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* jobs */}
          {report.jobs && (
            <div className="border-t border-emerald-300 pt-2">
              <div className="font-black text-emerald-900" data-testid="dedupe-jobs-summary">
                Jobs
              </div>
              <div>Duplicate groups: <b>{report.jobs.duplicate_groups}</b></div>
              <div>Duplicates found: <b>{report.jobs.duplicates_found}</b></div>
              <div>Archived this run: <b>{report.jobs.archived}</b></div>
              {report.jobs.samples?.length > 0 && (
                <div className="mt-1 pl-2 border-l-2 border-emerald-400">
                  <div className="text-[10px] uppercase text-emerald-700 font-black">Preview samples</div>
                  {report.jobs.samples.map((s, i) => (
                    <div key={i} className="mt-1" data-testid={`dedupe-job-sample-${i}`}>
                      <div><b>{s.title}</b> — {s.count}x · {s.status}</div>
                      <div className="text-[10px] text-slate-600">
                        @ {s.address_key} · keep {s.keep_id.slice(-6)} · drop {s.archive_ids.length}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* legacy merged + unit fixes */}
          <div className="border-t border-emerald-300 pt-2">
            <div>Legacy "merged"-status pairs: <b>{report.merged_pairs_found}</b> found · <b>{report.merged_deleted}</b> deleted</div>
            <div>"Unit Unit" prefix fixes: <b>{report.unit_prefix_fixed}</b></div>
            {report.unit_prefix_samples?.length > 0 && (
              <ul className="mt-1 pl-4 list-disc text-[10px] text-slate-600">
                {report.unit_prefix_samples.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function BulkAuditPanel({ slug }) {
  const [open, setOpen] = useState(false);
  const [secret, setSecret] = useState("");
  const [direct, setDirect] = useState("");
  const [subName, setSubName] = useState("");
  const [subAddrs, setSubAddrs] = useState("");
  const [lots, setLots] = useState("");
  const [sold, setSold] = useState("");
  const [multiFam, setMultiFam] = useState("");
  const [replace, setReplace] = useState(false);
  const [attachLawn, setAttachLawn] = useState(true);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const splitLines = t => t.split(/\r?\n/).map(x => x.trim()).filter(Boolean);

  // Parse multi-family lines like "1000-1002 S 52nd St | 1,2,3,4,5,6"
  const parseMultiFam = (text) => {
    const map = {};
    splitLines(text).forEach(line => {
      const [addr, units] = line.split("|").map(s => s.trim());
      if (addr && units) {
        map[addr] = units.split(",").map(u => u.trim()).filter(Boolean);
      }
    });
    return map;
  };

  const run = async () => {
    if (!secret) { toast.error("Paste the admin secret first"); return; }
    const body = {
      slug,
      direct: splitLines(direct),
      sub_holdings: subName && subAddrs
        ? { [subName.trim().toLowerCase().replace(/\s+/g, "_")]: splitLines(subAddrs) }
        : {},
      lots: splitLines(lots),
      remove_sold: splitLines(sold),
      multi_family_units: parseMultiFam(multiFam),
      attach_lots_to_lawn: attachLawn,
      replace_existing_keys: replace,
    };
    setRunning(true);
    setReport(null);
    try {
      const r = await api.post("/admin/portfolio-audit/run", body, {
        headers: { "X-Admin-Secret": secret },
      });
      setReport(r.data.report);
      toast.success("Audit complete");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Audit failed — check secret / endpoint enabled?"));
    } finally {
      setRunning(false);
    }
  };

  if (!open) {
    return (
      <div className="mt-6 border-t-2 border-dashed border-slate-200 pt-4">
        <button onClick={() => setOpen(true)}
          data-testid="bulk-audit-open"
          className="w-full h-11 border-2 border-indigo-500 bg-indigo-50 text-indigo-800 font-black uppercase text-[11px] tracking-widest hover:bg-indigo-100">
          ⚡ Bulk Audit From AppFolio List
        </button>
        <div className="text-[10px] text-slate-500 mt-1 text-center">
          Paste an address list and this portfolio is instantly rebuilt
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 border-2 border-indigo-500 bg-indigo-50 p-3 space-y-3" data-testid="bulk-audit-panel">
      <div className="flex items-center gap-2">
        <div className="font-black uppercase text-[11px] tracking-widest text-indigo-900">
          Bulk Audit — {slug}
        </div>
        <button onClick={() => setOpen(false)}
          className="ml-auto h-7 w-7 border-2 border-slate-300 bg-white hover:bg-slate-100 font-black">×</button>
      </div>

      <div>
        <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1 block">
          Admin secret (from Emergent Support)
        </label>
        <input type="password" value={secret} onChange={e => setSecret(e.target.value)}
          data-testid="bulk-audit-secret"
          placeholder="X-Admin-Secret from prod .env"
          className="w-full h-10 px-2 border-2 border-slate-300 text-sm font-mono"/>
      </div>

      <div>
        <label className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1 block">
          Direct addresses — one per line
        </label>
        <textarea value={direct} onChange={e => setDirect(e.target.value)} rows={6}
          data-testid="bulk-audit-direct"
          placeholder={"3816 W Girard Ave\n3866 W Girard Ave\n..."}
          className="w-full p-2 border-2 border-slate-300 text-xs font-mono"/>
        <div className="text-[10px] text-slate-500 mt-0.5">{splitLines(direct).length} addresses</div>
      </div>

      <details className="border border-slate-300 bg-white p-2" open={!!subName}>
        <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-slate-600">
          Optional: sub-holdings group (e.g. "AAR Holdings — controlled by Irfan")
        </summary>
        <div className="mt-2 space-y-2">
          <input value={subName} onChange={e => setSubName(e.target.value)}
            data-testid="bulk-audit-sub-name"
            placeholder="Group label — e.g. AAR Holdings Irfan"
            className="w-full h-9 px-2 border-2 border-slate-300 text-sm"/>
          <textarea value={subAddrs} onChange={e => setSubAddrs(e.target.value)} rows={4}
            data-testid="bulk-audit-sub-addrs"
            placeholder="Addresses under this sub-group, one per line"
            className="w-full p-2 border-2 border-slate-300 text-xs font-mono"/>
          <div className="text-[10px] text-slate-500">{splitLines(subAddrs).length} addresses</div>
        </div>
      </details>

      <details className="border border-slate-300 bg-white p-2">
        <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-slate-600">
          Optional: empty lots (subset — pulled from Direct + Sub above)
        </summary>
        <div className="mt-2 space-y-2">
          <textarea value={lots} onChange={e => setLots(e.target.value)} rows={4}
            data-testid="bulk-audit-lots"
            placeholder="Which of the above are empty lots?"
            className="w-full p-2 border-2 border-slate-300 text-xs font-mono"/>
          <label className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={attachLawn} onChange={e => setAttachLawn(e.target.checked)}/>
            Also add these lots to the Lawn Guru service coverage
          </label>
          <div className="text-[10px] text-slate-500">{splitLines(lots).length} lots</div>
        </div>
      </details>

      <details className="border border-slate-300 bg-white p-2">
        <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-slate-600">
          Optional: sold / to remove
        </summary>
        <textarea value={sold} onChange={e => setSold(e.target.value)} rows={3}
          data-testid="bulk-audit-sold"
          placeholder="Addresses to remove (only removes if previously tagged to this portfolio)"
          className="w-full p-2 border-2 border-slate-300 text-xs font-mono mt-2"/>
      </details>

      <details className="border border-slate-300 bg-white p-2">
        <summary className="cursor-pointer text-[10px] font-black uppercase tracking-widest text-slate-600">
          Optional: multi-family units — one per line: <code>ADDRESS | 1,2,3</code>
        </summary>
        <textarea value={multiFam} onChange={e => setMultiFam(e.target.value)} rows={6}
          data-testid="bulk-audit-multifam"
          placeholder={"1213 S 17th St | 1,2\n1000-1002 S 52nd St | 1,2,3,4,5,6\n4122 Ogden St | 1,2R,2F,3"}
          className="w-full p-2 border-2 border-slate-300 text-xs font-mono mt-2"/>
        <div className="text-[10px] text-slate-500 mt-1">
          Each address gets ONE property_config PER unit. Existing units skipped.
        </div>
      </details>

      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)}/>
        <span>Replace existing keys (uncheck to MERGE — safer)</span>
      </label>

      <button onClick={run} disabled={running || !secret}
        data-testid="bulk-audit-run"
        className="w-full h-11 bg-indigo-600 hover:bg-indigo-700 text-white font-black uppercase text-[11px] tracking-widest disabled:opacity-40">
        {running ? "Running…" : "Run Audit"}
      </button>

      {report && (
        <div className="p-3 border-2 border-emerald-400 bg-emerald-50 text-xs" data-testid="bulk-audit-report">
          <div className="font-black uppercase text-[10px] tracking-widest text-emerald-800 mb-1">
            Audit Complete
          </div>
          <div>Total properties: <b>{report.total_properties}</b></div>
          <div>Rental units: <b>{report.rental_units}</b> · Lots: <b>{report.lots}</b></div>
          <div>Linked existing: {report.linked_existing} · Created: {report.created} · Flagged as lots: {report.lots_flagged}</div>
          <div>Multi-family units created: {report.multi_family_units_created || 0}</div>
          <div>Removed sold: {report.removed_sold} · Added to lawn: {report.lawn_coverage_added}</div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase font-black text-slate-500 tracking-widest mb-1">{label}</div>
      {children}
    </label>
  );
}
